import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { SidebarProvider, useSidebar } from "./context/SidebarContext";
import { OrgProvider } from "./context/OrgContext";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetails from "./pages/ProjectDetails";
import TaskList from "./pages/TaskList";
import MyTasks from "./pages/MyTasks";
import Timeline from "./pages/Timeline";
import Companies from "./pages/Companies";
import Sectors from "./pages/Sectors";
import ModuleSelector from "./pages/ModuleSelector";
import PortfolioManagement from "./pages/PortfolioManagement";
import ActionItems from "./pages/ActionItems";
import MeetingManagement from "./pages/MeetingManagement";
import StrategyExecution from "./pages/StrategyExecution";
import StrategyDevelopment from "./pages/StrategyDevelopment";
import StrategyEditor from "./pages/StrategyEditor";
import Login from "./pages/Login";
import MGMTLogin from "./pages/MGMTLogin";
import Signup from "./pages/Signup";
import LandingPage from "./pages/LandingPage";
// PWA Pages
import MGMTApp from "./pages/pwa/MGMTApp";
import EscalationsList from "./pages/pwa/EscalationsList";
import ActionItemsList from "./pages/pwa/ActionItemsList";
import ProjectsList from "./pages/pwa/ProjectsList";
import SuperSearch from "./pages/pwa/SuperSearch";
import { Toaster } from "./components/ui/sonner";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-lg text-foreground">Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to module selector if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-lg text-foreground">Loading...</div>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/select-module" replace />;
};

// Public Route for MGMT Stack (redirect to PWA if authenticated)
const MGMTPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-lg text-foreground">Loading...</div>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/mgmt-app" replace />;
};

// Main App Layout with responsive sidebar
const AppLayout = ({ children, moduleType = 'project' }) => {
  const { user } = useAuth();
  const { collapsed } = useSidebar();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar currentUser={user} moduleType={moduleType} />
      <main 
        className={`flex-1 overflow-y-auto transition-all duration-300 ${
          collapsed ? 'ml-20' : 'ml-64'
        }`}
      >
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <OrgProvider>
            <SidebarProvider>
              <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <LandingPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/mgmt-login"
                element={
                  <MGMTPublicRoute>
                    <MGMTLogin />
                  </MGMTPublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <Signup />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              {/* Module Selector - after login */}
              <Route
                path="/select-module"
                element={
                  <ProtectedRoute>
                    <ModuleSelector />
                  </ProtectedRoute>
                }
              />
              {/* Homepage route removed as it is now public landing page */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Dashboard />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Projects />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/details"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ProjectDetails />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <TaskList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-tasks"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <MyTasks />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timeline"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Timeline />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timeline/:projectId"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Timeline />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Companies />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sectors"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Sectors />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portfolio"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="portfolio">
                      <PortfolioManagement />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              {/* Action Items Module */}
              <Route
                path="/action-items"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems showDashboard={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-items/all"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems viewMode="all" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-items/meetings-view"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems viewMode="collective" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-items/lists"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems viewMode="lists" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-items/manage-meetings"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems showManageMeetingsPage={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-items/mom"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems showMOMPage={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/action-items/manage-lists"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="action-items">
                      <ActionItems showManageListsPage={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Meeting Management Module */}
              <Route
                path="/meetings"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement showDashboard={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/all"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement viewMode="all" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/upcoming"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement viewMode="upcoming" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/past"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement viewMode="past" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/pending-approval"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement viewMode="pending-approval" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/calendar"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement showCalendar={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meetings/templates"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="meeting-management">
                      <MeetingManagement showTemplates={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <div className="p-8">
                        <h1 className="text-3xl font-bold">Team Management</h1>
                        <p className="text-muted-foreground mt-2">Coming soon...</p>
                      </div>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <div className="p-8">
                        <h1 className="text-3xl font-bold">Settings</h1>
                        <p className="text-muted-foreground mt-2">Coming soon...</p>
                      </div>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Strategy Execution Module */}
              <Route
                path="/strategy"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/sectors"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="sectors" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/companies"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="companies" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/functions"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="functions" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/scorecards"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="scorecards" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/dashboards"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="dashboards" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/kpis"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="kpis" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/kpis/:kpiId"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy">
                      <StrategyExecution defaultView="kpi-detail" />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Strategy Development Module */}
              <Route
                path="/strategy-development"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy-development">
                      <StrategyDevelopment />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy-development/new"
                element={
                  <ProtectedRoute>
                    <AppLayout moduleType="strategy-development">
                      <StrategyDevelopment autoCreate={true} />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy-development/:strategyId"
                element={
                  <ProtectedRoute>
                    <StrategyEditor />
                  </ProtectedRoute>
                }
              />

              {/* MGMT Stack PWA Routes */}
              <Route
                path="/mgmt-app"
                element={
                  <ProtectedRoute>
                    <MGMTApp />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mgmt-app/escalations"
                element={
                  <ProtectedRoute>
                    <EscalationsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mgmt-app/action-items"
                element={
                  <ProtectedRoute>
                    <ActionItemsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mgmt-app/projects"
                element={
                  <ProtectedRoute>
                    <ProjectsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mgmt-app/all-projects"
                element={
                  <ProtectedRoute>
                    <ProjectsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mgmt-app/search"
                element={
                  <ProtectedRoute>
                    <SuperSearch />
                  </ProtectedRoute>
                }
              />
              </Routes>
              <Toaster position="top-right" />
            </SidebarProvider>
            </OrgProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
